import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
  { label: "资讯", to: "/news/industry" },
  { label: "关于我们", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to.startsWith("/news")) return location.pathname.startsWith("/news");
    return location.pathname.startsWith(to.split("/").slice(0, 3).join("/"));
  };

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setKeyword("");
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200 bg-background-50/95 backdrop-blur-md">
      <div className="flex h-16 w-full items-center justify-between gap-4 px-4 md:px-8">
        <Link to="/" className="flex flex-shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-background-50">
            <i className="ri-movie-2-fill text-xl" aria-hidden="true" />
          </span>
          <span className="font-heading text-lg font-extrabold tracking-tight text-primary-800">
            黄色<span className="text-accent-500">在线</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                isActive(item.to)
                  ? "bg-primary-100 text-primary-800"
                  : "text-foreground-600 hover:bg-background-200 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="hidden items-center md:flex">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="flex items-center gap-2">
                <div className="relative">
                  <i
                    className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
                    aria-hidden="true"
                  />
                  <input
                    ref={inputRef}
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索影视或资讯"
                    className="w-52 rounded-md border border-background-300 bg-background-100 py-1.5 pl-9 pr-8 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
                  />
                  {keyword ? (
                    <button
                      type="button"
                      onClick={() => setKeyword("")}
                      aria-label="清除关键词"
                      className="absolute right-2 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700"
                    >
                      <i className="ri-close-line text-sm" aria-hidden="true" />
                    </button>
                  ) : null}
                </div>
                <button
                  type="submit"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-accent-600"
                >
                  搜索
                </button>
              </form>
            ) : (
              <Link
                to="/search"
                aria-label="进入搜索页"
                className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-200 hover:text-foreground-950"
              >
                <i className="ri-search-line text-lg" aria-hidden="true" />
              </Link>
            )}
          </div>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-200 md:hidden"
          >
            <i className="ri-search-line text-lg" aria-hidden="true" />
          </Link>

          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-200 lg:hidden"
          >
            <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
          <form onSubmit={submitSearch} className="relative mb-3">
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视或资讯"
              className="w-full rounded-md border border-background-300 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400 focus:bg-background-50"
            />
          </form>
          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive(item.to)
                    ? "bg-primary-100 text-primary-800"
                    : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true" />
              </Link>
            ))}
          </nav>
        </div>
      ) : null}
    </header>
  );
}